import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Compass, Cpu, FileText, Mail, Map, Quote, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";

import Breadcrumb from "@/components/Breadcrumb";
import SectionNav from "@/components/SectionNav";
import MilestonesInProgress from "@/components/MilestonesInProgress";
import { IMG } from "@/data/imageLibrary";
import { AI_TEAM, TEAM_WORKSPACE_PHOTOS, ROLE_THEME } from "@/data/aiTeam";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import JapanGraphic from "@/components/motion/JapanGraphic";
import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { breadcrumbSchema, personSchema } from "@/components/JsonLd";
import { PAGE_META } from "@/data/pageMeta";
import useDocumentHead from "@/lib/useDocumentHead";
import {
  ServiceVisualStyles,
  OrbitingNodes,
  RadarSweep,
} from "@/components/ServiceVisuals";
import ServiceFootprint from "@/components/ServiceFootprint";

/**
 * Consolidated About page — Vision, Values, Pillars, Message, and
 * History on one scrollable page with anchored sections. The dedicated
 * Corporate Profile route (`/:lang/corporate-profile`) carries the
 * legal/registration/capital facts that used to live here.
 */
export default function AboutPage({ lang }) {
  const isJa = lang === "ja";
  // Title + meta — owned by <SEO /> via Helmet (see top of returned JSX).

  const sections = isJa
    ? [
        { id: "vision", label: "企業理念" },
        { id: "values", label: "5つの価値" },
        { id: "pillars", label: "3つの柱" },
        { id: "message", label: "代表メッセージ" },
        { id: "engine", label: "AI支援基盤" },
      ]
    : [
        { id: "vision", label: "Vision" },
        { id: "values", label: "Values" },
        { id: "pillars", label: "Pillars" },
        { id: "message", label: "Message" },
        { id: "engine", label: "AI Engine" },
      ];

  const values = isJa
    ? [
        { title: "実務から考える", body: "現場で何が困っているかから設計を始めます。" },
        { title: "わかりやすく伝える", body: "難しい仕組みは、わかりやすい言葉と画面に。" },
        { title: "信頼を守る", body: "大切な情報は、安全な画面の中だけで扱います。" },
        { title: "多言語で支える", body: "日本語と英語で、お客様の手続きを支援します。" },
        { title: "長期的に改善する", body: "短期の派手さよりも、長く続く価値を選びます。" },
      ]
    : [
        { title: "Start from operations", body: "Design begins with what users actually struggle with on the ground." },
        { title: "Make things understandable", body: "Hard systems translated into clear language and clear screens." },
        { title: "Protect trust", body: "Sensitive information stays inside secure screens, not casual channels." },
        { title: "Support every language", body: "Japanese and English support for everyday procedures." },
        { title: "Improve long-term", body: "We choose lasting value over short-term flash." },
      ];

  const pillars = [
    { key: "mobility", accent: "#5B2E83", icon: Map, labelEn: "Mobility", labelJa: "モビリティ", detailJa: "ETC・道路インフラを誰もが使いやすく。", detailEn: "Making ETC and Japan's expressway infrastructure easier to access for everyone." },
    { key: "records", accent: "#2F8F3C", icon: FileText, labelEn: "Records", labelJa: "記録", detailJa: "利用記録の整理・レポートで業務を支援。", detailEn: "Organizing usage records and producing clear reports for daily operations." },
    { key: "digital", accent: "#0E5FB0", icon: Sparkles, labelEn: "Digital Infrastructure", labelJa: "デジタル基盤", detailJa: "決済・データ整理・多言語対応を、長く使える基盤に。", detailEn: "Payment, data organization and multilingual access — connected into long-lasting infrastructure." },
  ];

  return (
    <article data-testid="about-page" className="bg-white">
      <SEO {...PAGE_SEO.about[lang]} path={`/${lang}/about`} lang={lang} />
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
            { name: isJa ? "会社情報" : "About", url: `/${lang}/about` },
          ]),
          personSchema({
            name: "Adam Jones",
            jobTitle: isJa ? "創業者・代表取締役" : "Founder & Representative Director",
            image: "/founder/adam-jones.png",
          }),
        ]}
      />
      <Breadcrumb lang={lang} crumbs={[{ label: isJa ? "会社情報" : "About" }]} />

      {/* HERO — Adam portrait + founder-message snippets */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 -z-10 nojo-radial-light" />
        <JapanGraphic variant="asanoha" color="#5B2E83" className="absolute inset-0 w-full h-full opacity-15 pointer-events-none" data-testid="about-hero-asanoha" />
        <BrandGraphic variant="orbit" color="#5B2E83" className="hidden lg:block absolute -top-12 -right-12 w-80 h-80 opacity-20 pointer-events-none" data-testid="about-hero-orbit" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6 md:pt-8 pb-10 md:pb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <Reveal as="div" className="lg:col-span-7" variant="up">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] font-bold uppercase">
              {isJa ? "会社情報｜創業者の歩み" : "About NoJo Enterprise · A founder's story"}
            </div>
            <h1 className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.08]"].join(" ")}>
              {isJa
                ? "1人の覚悟から、13年。横須賀発、2026年6月に18,000名超のお客様へ到達。"
                : "From one person's resolve to 18,000+ customers across Japan — reached in June 2026 as a major milestone, thirteen years and counting."}
            </h1>
            <p className="mt-4 text-[15px] md:text-[16px] leading-[1.8] text-[#475569] max-w-2xl">
              {isJa
                ? "NoJo Enterpriseは、創業者アダム・ジョーンズによって2013年に設立されました。しかし、本当の挑戦が始まったのは、その2年前 ─ 2011年です。日本で暮らす外国人居住者、米軍関係者とそのご家族、外交官、宣教師、外資系企業のために、英語で利用できるETCサービスを実現したい。その想いを抱いて、2011年、2012年、2013年と関係機関を回り続け、断られ続けました。12回目までは「No」 ─ そして13回目で、ようやく「Yes」が返ってきました。ただし、それはゴールではなくスタートライン。当社と関係機関のシステムを安全につなぐために、さらに8か月の開発期間が必要でした。その後、ごく限られた台数からパイロット運用を始め、品質を確かめながら、ゆっくりと、しかし着実に拡張を重ねていきました。あれから13年。2026年6月、日本全国で18,000名を超えるお客様にご利用いただくという大きな節目を迎えました。次世代のJapanETCcardサイトと、2026年後半に予定されているJapanTollReceiptsの新プラットフォームの開発も、現在進行中です。"
                : "NoJo Enterprise was founded by Adam Jones in 2013 — but the real work began two years earlier. In 2011 we started knocking on doors. Through 2011, 2012, and into 2013 we approached banks, financial institutions, credit unions, NEXCO and the official government channels trying every legitimate path to build an English-language ETC service for foreign residents, U.S. military families, diplomats, missionaries, and international businesses in Japan. Twelve times we were told no. On the thirteenth try, someone finally said yes — but a \"yes\" was only the starting line. It took another eight months of engineering to safely connect our systems to theirs, then a deliberately small pilot run before we were allowed to grow customer by customer, slowly and cleanly. Thirteen years on from that first yes, we reached a major milestone in June 2026 — over 18,000 customers nationwide. Development is now underway on the next-generation JapanETCcard site and an enhanced JapanTollReceipts platform expected to launch later in 2026."}
            </p>
            <p className="mt-4 text-[14.5px] md:text-[15.5px] leading-[1.85] text-[#475569] max-w-2xl">
              {isJa
                ? "「一人でここまで来られた」とは、けっして言えません。NoJo Enterpriseは、創業当初から、強い責任感を持つチームに支えられてきました。営業、運営、現場サポート、開発、日本側のパートナーシップ ─ それぞれの責任を担い、迷いなく動いてくれている全員のおかげで、私たちは今この場所に立っています。現在のチーム力を大切にしながら、これからも責任ある運営を続けていきます。"
                : "I didn't get here alone. NoJo Enterprise has been a tight, deeply committed team from day one — sales, operations, on-the-ground support, engineering, and the partners on the Japan side who carry their part of the load without flinching. Our team is strong today, and we will keep being strong because of it."}
            </p>

            {/* Pull quotes — snippets from both founder messages */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                className="rounded-2xl p-4 ring-1 ring-[#E9DCF5]"
                style={{ backgroundColor: "#F4EEF9" }}
                data-testid="about-snippet-jec"
              >
                <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#5B2E83] mb-1.5">
                  {isJa ? "JapanETCcardの原点" : "Why JapanETCcard"}
                </div>
                <p className={["italic text-[13.5px] leading-[1.7] text-[#0F1D2D]", isJa ? "font-ja" : ""].join(" ")}>
                  {isJa
                    ? "「私自身が、その『信頼』になるしかなかった。」"
                    : "“I had to become the trust — personally responsible for every foreign driver in Japan.”"}
                </p>
              </div>
              <div
                className="rounded-2xl p-4 ring-1 ring-[#CFE7D4]"
                style={{ backgroundColor: "#EFF7F0" }}
                data-testid="about-snippet-jtr"
              >
                <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#2F8F3C] mb-1.5">
                  {isJa ? "JapanTollReceiptsの原点" : "Why JapanTollReceipts"}
                </div>
                <p className={["italic text-[13.5px] leading-[1.7] text-[#0F1D2D]", isJa ? "font-ja" : ""].join(" ")}>
                  {isJa
                    ? "「ソフトウェアの構想ではなく、『困りごと』から始まりました。」"
                    : "“This did not begin as a software idea. It began with pain.”"}
                </p>
              </div>
            </div>

            {/* Dual CTAs to both founder messages */}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/${lang}/founders-message/japanetccard`}
                data-testid="about-cta-founder-jec"
                className="inline-flex items-center gap-2 bg-[#5B2E83] text-white px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight hover:opacity-90 transition-opacity"
              >
                {isJa
                  ? "代表メッセージを読む・JapanETCcard"
                  : "Read the Founder's Message · JapanETCcard"}
                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
              </Link>
              <Link
                to={`/${lang}/founders-message/japantollreceipts`}
                data-testid="about-cta-founder-jtr"
                className="inline-flex items-center gap-2 px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight ring-1 ring-[#2F8F3C] text-[#2F8F3C] hover:bg-[#EFF7F0] transition-colors"
              >
                {isJa
                  ? "代表メッセージを読む・JapanTollReceipts"
                  : "Read the Founder's Message · JapanTollReceipts"}
                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
              </Link>
            </div>

            {/* COMPANY AT A GLANCE — moved from below into the left column
                so the left side fills the empty space and aligns its bottom
                with the blue milestones card on the right. */}
            <div className="mt-6 pt-5 border-t border-[#E2E8F0]" data-testid="about-glance">
              <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold mb-1.5">
                {isJa ? "NoJo Enterpriseの概要" : "Company at a glance"}
              </div>
              <h2 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[19px] md:text-[21px] font-bold tracking-tight leading-snug mb-4"].join(" ")}>
                {isJa ? "私たちが何者で、どこから来て、何を目指しているか。" : "Who we are, where we come from, and where we are going."}
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4" data-testid="about-glance-tiles">
                {[
                  { key: "vision", anchor: "#vision", color: "#5B2E83", Icon: Compass, labelEn: "Vision", labelJa: "企業理念", bodyEn: "Mission, values and the three pillars that guide our work.", bodyJa: "ミッション、バリュー、そして3つの柱。" },
                  { key: "message", anchor: "#message", color: "#0E5FB0", Icon: Quote, labelEn: "Representative Message", labelJa: "代表メッセージ", bodyEn: "A short note from Adam Jones, our Representative Director.", bodyJa: "代表取締役 アダム ジョーンズからのメッセージ。" },
                  { key: "profile", anchor: `/${lang}/corporate-profile`, color: "#2F8F3C", Icon: FileText, labelEn: "Corporate Profile", labelJa: "会社概要", bodyEn: "Registered name, founded date, address, capital and registration.", bodyJa: "商号、設立、所在地、資本金、登録番号。" },
                  { key: "engine", anchor: "#engine", color: "#0F1D2D", Icon: Cpu, labelEn: "AI Engine", labelJa: "AI支援基盤", bodyEn: "The trained five-member AI team that powers every workflow.", bodyJa: "全業務を支える、訓練された5つのAI支援ワークフロー。" },
                ].map((t) => (
                  <li key={t.key}>
                    <a
                      href={t.anchor}
                      data-testid={`about-glance-${t.key}`}
                      className="group h-full block bg-white rounded-2xl border border-[#E2E8F0] p-4 md:p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-22px_rgba(15,29,45,0.25)] hover:border-transparent relative overflow-hidden"
                    >
                      <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl" style={{ backgroundColor: t.color }} />
                      <span aria-hidden className="inline-flex items-center justify-center h-8 w-8 rounded-md" style={{ backgroundColor: `${t.color}15` }}>
                        <t.Icon className="h-4 w-4" strokeWidth={1.7} style={{ color: t.color }} />
                      </span>
                      <h3 className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[15px] font-bold tracking-tight leading-snug"].join(" ")}>
                        {isJa ? t.labelJa : t.labelEn}
                      </h3>
                      <p className="mt-1.5 text-[12px] leading-[1.65] text-[#475569]">
                        {isJa ? t.bodyJa : t.bodyEn}
                      </p>
                      <span className="mt-2.5 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold transition-colors" style={{ color: t.color }}>
                        {isJa ? "詳しく" : "Explore"}
                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* RIGHT — Adam portrait */}
          <Reveal as="div" className="lg:col-span-5" variant="scale" delay={150}>
            <div className="relative w-full max-w-[400px] mx-auto lg:mx-0 lg:ml-auto">
              <Link
                to={`/${lang}/our-leaders`}
                aria-label={isJa ? "リーダーシップチームを見る" : "Meet the leadership team"}
                className="group relative block aspect-[4/5] w-full overflow-hidden rounded-2xl ring-2 ring-[#5B2E83] transition-shadow hover:ring-[#7A4DA8] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#5B2E83]/40"
                style={{ boxShadow: "0 30px 70px -30px rgba(91,46,131,0.45)" }}
                data-testid="about-founder-portrait"
              >
                <img
                  src="/founder/adam-jones.png"
                  alt={isJa ? "Adam Jones、NoJo Enterprise 創業者兼CEO" : "Adam Jones — Founder & CEO, NoJo Enterprise"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="eager"
                />
                <span
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 55%, rgba(15,29,45,0.78) 100%)",
                  }}
                />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white ring-1 ring-white/20 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold">
                    {isJa ? "創業者 ・ CEO" : "Founder & CEO"}
                  </span>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="font-display text-white text-[22px] font-bold tracking-tight">
                      Adam Jones
                    </div>
                    <ArrowUpRight
                      className="h-4 w-4 text-white opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </div>
                </div>
              </Link>
              <Link
                to={`/${lang}/our-leaders`}
                data-testid="about-cta-leaders"
                className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#5B2E83] hover:underline"
              >
                {isJa ? "リーダーシップチームを見る" : "Meet the leadership team"}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </Link>

              {/* MILESTONES infographic — fills the empty space below
                  Adam's photo with the journey at a glance. Owner directive
                  Feb 2026: pull key dates from japancardetc.com/about, put
                  them on the right column under the leadership link, in a
                  brand-distinct color so it pops. */}
              <div
                data-testid="about-milestones-card"
                className="mt-6 rounded-2xl p-5 md:p-6 ring-1 relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg,#0E5FB0 0%,#0A4585 60%,#1F1733 100%)",
                  borderColor: "rgba(255,255,255,0.18)",
                }}
              >
                <span
                  aria-hidden
                  className="absolute -top-12 -right-12 h-40 w-40 rounded-full opacity-30 blur-3xl"
                  style={{ backgroundColor: "#7AB8E8" }}
                />
                <div className="relative">
                  <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                    {isJa ? "歩んできた道のり" : "Milestones along the way"}
                  </div>
                  <h3
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "mt-1.5 text-white text-[18px] md:text-[20px] font-bold tracking-tight leading-tight",
                    ].join(" ")}
                  >
                    {isJa
                      ? "13年で、420名から18,000名以上のお客様へ(2026年6月の節目)。"
                      : "From 420 customers to 18,000+ — a June 2026 milestone, over thirteen years."}
                  </h3>
                  <ul className="mt-5 space-y-3" data-testid="about-milestones-list">
                    {[
                      {
                        year: "2013",
                        en: "Founded in Yokosuka — first 420 customers signed up the launch year.",
                        ja: "横須賀で創業 ─ 開業年に最初の420名のお客様が登録。",
                      },
                      {
                        year: "2014",
                        en: "First nationwide billboard campaign reached international communities across Japan.",
                        ja: "全国規模の広告キャンペーンを初実施。",
                      },
                      {
                        year: "2016",
                        en: "Service expanded to all U.S. military installations in Japan.",
                        ja: "日本国内すべての米軍施設にサービスを拡大。",
                      },
                      {
                        year: "2018",
                        en: "Crossed the 5,000-customer milestone, expanded to embassies.",
                        ja: "ご利用者数5,000名を突破。各国大使館へも展開。",
                      },
                      {
                        year: "2020",
                        en: "Official ETC partner for NBC Olympics — Tokyo 2020 Games.",
                        ja: "NBC OlympicsのETCパートナーとして、東京2020大会に参画。",
                      },
                      {
                        year: "2023",
                        en: "Passed 10,000 active customers; covered every U.S. mission in Japan.",
                        ja: "アクティブご利用者10,000名超 ─ 米国の全在外公館にカバレッジ拡大。",
                      },
                      {
                        year: "2025",
                        en: "12-year anniversary; first formal meeting with the senior leadership who steward ETC nationally.",
                        ja: "創業12周年。日本のETC制度を国レベルで支える上層部と初の公式面談。",
                      },
                      {
                        year: "2026",
                        en: "Full platform overhaul — AI Operations Division, JTR Bridge API and corporate website launch.",
                        ja: "プラットフォーム全面刷新 ─ AIオペレーション部門、JTR Bridge API、コーポレートサイト公開。",
                      },
                      {
                        // Continuation bullet under 2026 — no separate year pill
                        // per owner directive: no month labels in the timeline,
                        // group June milestone visually under the same 2026 badge.
                        year: "2026",
                        continuation: true,
                        en: "18,000-customer milestone reached across Japan — thirteen years of steady, disciplined growth.",
                        ja: "日本全国で18,000名到達 ─ 13年間の着実な成長を経て大きな節目を迎えました。",
                      },
                    ].map((m, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3"
                        data-testid={
                          m.continuation
                            ? `about-milestone-${m.year}-continuation`
                            : `about-milestone-${m.year}`
                        }
                      >
                        {m.continuation ? (
                          // Invisible spacer — same width as the year pill so
                          // the text lines up cleanly with the row above.
                          <span
                            aria-hidden="true"
                            className="shrink-0 inline-flex items-center justify-center min-w-[44px] h-[22px] px-2"
                          />
                        ) : (
                          <span
                            className="shrink-0 inline-flex items-center justify-center min-w-[44px] h-[22px] px-2 rounded-full text-[10.5px] tracking-[0.08em] font-mono-display font-bold"
                            style={{
                              color: "#0F1D2D",
                              backgroundColor: "#7AB8E8",
                            }}
                          >
                            {m.year}
                          </span>
                        )}
                        <p className="text-[12.5px] leading-[1.65] text-white/90">
                          {m.continuation && (
                            // Small dot to signal "second bullet under
                            // the same year" so readers see it as a
                            // continuation, not an orphaned line.
                            <span
                              aria-hidden="true"
                              className="inline-block mr-2 text-[#7AB8E8]"
                            >
                              •
                            </span>
                          )}
                          {isJa ? m.ja : m.en}
                        </p>
                      </li>
                    ))}
                  </ul>
                  {/* Headline metrics bar */}
                  <div className="mt-6 pt-5 border-t border-white/15 grid grid-cols-3 gap-3">
                    <div>
                      <div className="font-display text-white text-[18px] font-bold tracking-tight leading-none">
                        18,000+
                      </div>
                      <div className="mt-1 text-[9.5px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                        {isJa ? "ご利用者" : "Customers"}
                      </div>
                    </div>
                    <div>
                      <div className="font-display text-white text-[18px] font-bold tracking-tight leading-none">
                        13+
                      </div>
                      <div className="mt-1 text-[9.5px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                        {isJa ? "運営年数" : "Years"}
                      </div>
                    </div>
                    <div>
                      <div className="font-display text-white text-[18px] font-bold tracking-tight leading-none">
                        37
                      </div>
                      <div className="mt-1 text-[9.5px] tracking-[0.16em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                        {isJa ? "米軍施設" : "Installations"}
                      </div>
                    </div>
                  </div>
                </div>
                <MilestonesInProgress lang={lang} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION NAV (in-page) */}
      <SectionNav sections={sections} lang={lang} tone="#5B2E83" pageKey="about" />

      {/* VISION */}
      <section id="vision" data-testid="about-vision" className="bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold">
              <Compass className="h-3.5 w-3.5" strokeWidth={2} />
              {isJa ? "企業理念" : "Vision"}
            </div>
            <h2 className={["mt-4", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15]"].join(" ")}>
              {isJa ? "ミッションとビジョン" : "Mission & Vision"}
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-10">
            <div>
              <div className="text-[10px] tracking-[0.22em] font-mono-display text-[#5B2E83] uppercase font-bold">
                Mission
              </div>
              <p className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-2xl md:text-3xl font-bold tracking-tight leading-[1.25]"].join(" ")}>
                {isJa ? "日本の複雑な仕組みを、誰もが使いやすいサービスへ。" : "Turning complex systems in Japan into practical services people can actually use."}
              </p>
              <p className="mt-4 text-[15px] leading-[1.85] text-[#475569] max-w-3xl">
                {isJa
                  ? "ETCカード、利用記録、外貨両替、各種手続きには、言語の壁、情報の分かりにくさ、管理の難しさがあります。NoJo Enterpriseは、その一つひとつの不便を現場から見つけ、実際に使えるサービスとして形にしていきます。"
                  : "ETC cards, usage records, foreign exchange procedures and other administrative work in Japan come with language barriers, unclear information and difficult management. We identify these frictions on the ground and turn them, one by one, into services people can actually use."}
              </p>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] font-mono-display text-[#0E5FB0] uppercase font-bold">
                Vision
              </div>
              <p className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-2xl md:text-3xl font-bold tracking-tight leading-[1.25]"].join(" ")}>
                {isJa ? "移動・記録・デジタル基盤の分野で、信頼される実務支援の基盤をつくる。" : "Building trusted practical-service infrastructure across mobility, records and digital infrastructure in Japan."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section id="values" data-testid="about-values" className="relative isolate bg-white">
        <PageSideAura testId="about-values-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#2F8F3C] uppercase font-bold mb-3">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
            {isJa ? "5つの価値" : "Values"}
          </div>
          <h2 className={["mb-10", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] max-w-2xl"].join(" ")}>
            {isJa ? "私たちが大切にする5つの姿勢。" : "Five operating principles we hold to."}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-[#E2E8F0] border-y border-[#E2E8F0]">
            {values.map((v, i) => (
              <Reveal as="li" variant="up" delay={i * 90} key={i} data-testid={`about-value-${i + 1}`} className="py-7 lg:py-0 lg:px-6 first:lg:pl-0 last:lg:pr-0 lg:py-7">
                <div className="font-mono-display text-[14px] tracking-[0.22em] text-[#5B2E83] font-bold uppercase">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className={["mt-3", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[17px] font-bold tracking-tight leading-snug"].join(" ")}>
                  {v.title}
                </h3>
                <p className="mt-2.5 text-[13.5px] leading-[1.8] text-[#475569]">
                  {v.body}
                </p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars" data-testid="about-pillars" className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold mb-3">
            {isJa ? "3つの柱" : "Pillars"}
          </div>
          <h2 className={["mb-10", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] max-w-2xl"].join(" ")}>
            {isJa ? "モビリティ・記録・デジタル基盤の3領域で社会を支える。" : "Three pillars supporting Japan: mobility, records, digital infrastructure."}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2E8F0] border-y border-[#E2E8F0]">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.key} variant="scale" delay={i * 120} data-testid={`about-pillar-${p.key}`} className="py-8 md:py-10 md:px-8 first:md:pl-0 last:md:pr-0">
                  <span aria-hidden className="inline-flex items-center justify-center h-12 w-12 animate-brand-float" style={{ backgroundColor: `${p.accent}15`, animationDelay: `${i * 250}ms` }}>
                    <Icon className="h-6 w-6" strokeWidth={1.5} style={{ color: p.accent }} />
                  </span>
                  <div className="mt-5 text-[10px] tracking-[0.26em] font-mono-display uppercase font-bold" style={{ color: p.accent }}>
                    {`0${i + 1} · ${isJa ? p.labelJa : p.labelEn}`}
                  </div>
                  <h3 className={["mt-2", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[22px] font-bold tracking-tight leading-snug"].join(" ")}>
                    {isJa ? p.labelJa : p.labelEn}
                  </h3>
                  <p className="mt-3 text-[13.5px] leading-[1.8] text-[#475569] max-w-xs">
                    {isJa ? p.detailJa : p.detailEn}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MESSAGE */}
      <section id="message" data-testid="about-message" className="relative isolate bg-white">
        <PageSideAura testId="about-message-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            {/* Animated visualization — replaces the previous static
                cityscape photo per owner directive. The 5 orbiting nodes
                represent the AI Operations team revolving around the
                founder's promise; reinforced visually with a slow radar
                sweep beneath it.                                          */}
            <div
              data-testid="about-message-visual"
              className="aspect-[5/6] w-full rounded-3xl overflow-hidden ring-1 ring-[#E9D8F4] bg-gradient-to-br from-[#F4EEF9] via-white to-[#EFF8FF] p-6 md:p-8 flex flex-col"
            >
              <ServiceVisualStyles />
              <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#5B2E83] mb-4">
                {isJa ? "私たちの運用ディシプリン" : "Our operating discipline"}
              </div>
              <div className="flex-1 min-h-0 grid grid-rows-2 gap-4">
                <div className="rounded-2xl bg-white/70 ring-1 ring-[#E2E8F0] p-3 flex items-center justify-center">
                  <OrbitingNodes accent="#5B2E83" />
                </div>
                <div className="rounded-2xl bg-white/70 ring-1 ring-[#E2E8F0] p-3 flex items-center justify-center">
                  <RadarSweep accent="#0E5FB0" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#E2E8F0] text-[11px] leading-relaxed text-[#64748B]">
                {isJa
                  ? "5つのAI支援ワークフローが、3つのサービスの運用を支えています。"
                  : "Five-member AI team. Three services. Operated around the clock."}
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#0F1D2D] uppercase font-bold">
              <Quote className="h-3.5 w-3.5" strokeWidth={2} />
              {isJa ? "代表メッセージ" : "Message"}
            </div>
            <h2 className={["mt-4", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15]"].join(" ")}>
              {isJa ? "現場から考え続ける、ということ。" : "Continuing to think from the ground up."}
            </h2>
            <div className="mt-6 space-y-5 text-[15px] leading-[1.95] text-[#475569] max-w-2xl">
              <p>
                {isJa
                  ? "私たちは2013年、横須賀の小さなオフィスから始まりました。最初のお客様は、日本語の手続きに困っていた数名の外国人ドライバーでした。"
                  : "We started in a small Yokosuka office in 2013. Our first customers were a handful of foreign drivers struggling with Japanese-language procedures."}
              </p>
              <p>
                {isJa
                  ? "13年が経ち、日本全国のお客様に支えられるサービスへと成長しました。けれど私たちのやり方は同じです。お客様の困りごとから始め、地道に運用を改善し、長く続く仕組みに落とし込む。NoJo Enterpriseは、これからもこの姿勢を変えません。"
                  : "Thirteen years later, we have built a focused customer base across Japan that trusts us. But our approach has not changed. Start from the customer's friction. Improve operations patiently. Convert it into infrastructure that lasts. NoJo Enterprise will keep that posture."}
              </p>
            </div>
            <div className="mt-7 pt-5 border-t border-[#E2E8F0]">
              <div className="font-display text-[#0F1D2D] text-[15px] font-bold tracking-tight">
                {isJa ? "ジョーンズ・アダム" : "Adam Jones"}
              </div>
              <div className="text-[11px] tracking-[0.18em] font-mono-display text-[#64748B] uppercase font-semibold mt-1">
                {isJa ? "代表取締役" : "Representative Director"}
              </div>
            </div>

            {/* Founder snippet cards — moved under the signature, laid
                side-by-side per owner directive ("move these to the
                right underneath Adam Jones representative director and
                just have them side-by-side so it doesn't go too
                vertically down"). Each card is a pull-quote linking
                to the relevant founder's message.                       */}
            <div
              className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4"
              data-testid="about-message-founder-snippets"
            >
              <Link
                to={`/${lang}/founders-message/japanetccard`}
                data-testid="about-message-jec-snippet"
                className="group block rounded-2xl ring-1 ring-[#E9D8F4] bg-[#F4EEF9] p-4 transition-all hover:ring-transparent hover:shadow-[0_18px_36px_-22px_rgba(91,46,131,0.4)] hover:-translate-y-0.5"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#5B2E83]">
                    {isJa ? "JapanETCcardの物語" : "JapanETCcard story"}
                  </div>
                  <ArrowRight
                    className="h-3.5 w-3.5 text-[#5B2E83] opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0"
                    strokeWidth={2.25}
                  />
                </div>
                <p className="text-[12.5px] leading-[1.7] text-[#1F2937] italic">
                  {isJa
                    ? "「最初の12回はすべて『無理だ』。13回目で『やってみよう』と言ってくれる方に出会えた ─ そこからこの会社は始まりました。」"
                    : "\"My first twelve answers were all 'impossible.' The thirteenth was 'let's try' — and that one yes is where this company began.\""}
                </p>
                <span className="mt-2.5 inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#5B2E83] tracking-tight">
                  {isJa ? "全文を読む" : "Read the full story"}
                  <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
                </span>
              </Link>

              <Link
                to={`/${lang}/founders-message/japantollreceipts`}
                data-testid="about-message-jtr-snippet"
                className="group block rounded-2xl ring-1 ring-[#CDE7CC] bg-[#ECF6EC] p-4 transition-all hover:ring-transparent hover:shadow-[0_18px_36px_-22px_rgba(47,143,60,0.4)] hover:-translate-y-0.5"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#2F8F3C]">
                    {isJa ? "JapanTollReceiptsの物語" : "JapanTollReceipts story"}
                  </div>
                  <ArrowRight
                    className="h-3.5 w-3.5 text-[#2F8F3C] opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0"
                    strokeWidth={2.25}
                  />
                </div>
                <p className="text-[12.5px] leading-[1.7] text-[#1F2937] italic">
                  {isJa
                    ? "「お客様がかつて何週間も追いかけ続けていた『月末の通行領収書』が、いまでは自動で、毎月、メールボックスに届きます。」"
                    : "\"The month-end toll receipt customers used to chase for weeks now arrives in their inbox automatically — every month, on schedule.\""}
                </p>
                <span className="mt-2.5 inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#2F8F3C] tracking-tight">
                  {isJa ? "全文を読む" : "Read the full story"}
                  <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROFILE — REMOVED (moved to dedicated /corporate-profile page per owner directive).
       * The About page now defers all capital/legal-name/registration facts to the
       * Corporate Profile route; About focuses on narrative (vision/values/pillars/message/history).
       */}

      {/* SERVICE FOOTPRINT — communities we support across Japan.
       * Owner directive Feb 2026: surface a brief summary + outbound links
       * to each japancardetc.com directory (installations, embassies,
       * foreign embassies, defense contractors, MWR, exchange, religious,
       * USO) directly below the founder's message. Avoid inlining every
       * 37-installation row — link out instead. Closes with "and many,
       * many more." */}
      <ServiceFootprint lang={lang} />

      {/* AI ENGINE — explains how NoJo is actually able to run every service.
       *  Replaces the old 2013 history timeline per owner directive (Feb 2026):
       *  the team that actually powers the operations is more important than a
       *  marketing year-list. The 5-member trained-LLM team is the backbone.
       */}
      <section id="engine" data-testid="about-engine" className="relative bg-[#3F1F5B] text-white overflow-hidden">
        <BrandGraphic
          variant="wave"
          color="#7AB8E8"
          className="absolute inset-0 w-full h-full opacity-[0.10] pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          {/* Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#7AB8E8] uppercase font-bold mb-4">
                <Cpu className="h-3.5 w-3.5" strokeWidth={2} />
                {isJa ? "私たちのAI支援基盤" : "How NoJo actually runs"}
              </div>
              <h2 className={[isJa ? "font-ja" : "font-display", "text-white text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.12]"].join(" ")}>
                {isJa
                  ? "毎日の処理は、目的別に設計された5つのAI支援ワークフローが支えています。"
                  : "Every workflow we run is operated by a trained five-member AI team."}
              </h2>
              <p className="mt-5 text-[15px] leading-[1.9] text-white/75 max-w-2xl">
                {isJa
                  ? "ETCカード、通行記録、準備中の両替サービス。各処理は、目的別に設計された5つのAI支援ワークフローが支えています。3つのアナリストロールが分類・抽出・整理を支援し、QAリードのリンが整合性を確認し、必要な内容は人による最終確認を経てお客様に届きます。"
                  : "ETC cards, toll records, the planned exchange service — every automated task is handled by the same five-member AI team we trained, role by role. Three Senior Analysts classify, extract and interpret. Rin (QA Lead) cross-checks consistency. Sora (Director) gives the final go-ahead before anything reaches a customer."}
              </p>
            </div>
            <div className="lg:col-span-5">
              <ul className="grid grid-cols-3 gap-3">
                <li className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4 text-center">
                  <div className="font-display text-3xl font-bold text-white">3</div>
                  <div className="mt-1 text-[10px] tracking-[0.18em] font-mono-display text-[#2F8F3C] uppercase font-bold">
                    {isJa ? "アナリスト" : "Analysts"}
                  </div>
                </li>
                <li className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4 text-center">
                  <div className="font-display text-3xl font-bold text-white">1</div>
                  <div className="mt-1 text-[10px] tracking-[0.18em] font-mono-display text-[#7AB8E8] uppercase font-bold">
                    {isJa ? "QA リード" : "QA Lead"}
                  </div>
                </li>
                <li className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4 text-center">
                  <div className="font-display text-3xl font-bold text-white">1</div>
                  <div className="mt-1 text-[10px] tracking-[0.18em] font-mono-display text-[#F59E0B] uppercase font-bold">
                    {isJa ? "ディレクター" : "Director"}
                  </div>
                </li>
              </ul>
              <p className="mt-4 text-[12px] leading-[1.7] text-white/80 max-w-md">
                {isJa
                  ? "各AIロールは、扱う書類・ルール・確認基準ごとに役割を分けて設計されています。"
                  : "Each member is trained for a specific class of documents, rules and judgment criteria — by design, not by accident."}
              </p>
            </div>
          </div>

          {/* AI workspace editorial photos — actually shows the team operating
              the dashboards. Replaces generic stock photos with on-brand imagery. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-12">
            {TEAM_WORKSPACE_PHOTOS.slice(0, 2).map((p, i) => (
              <Reveal as="div" variant="up" delay={i * 100} key={p.id}>
                <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[4/3]">
                  <img
                    src={p.src}
                    alt={isJa ? p.captionJa : p.captionEn}
                    className="block w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#5B2E83] via-transparent to-transparent opacity-80" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                      {isJa ? p.captionJa : p.captionEn}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* What each model is trained on */}
          <div className="mb-12">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#7AB8E8] uppercase font-bold mb-4">
              {isJa ? "それぞれの専門領域" : "What each model is trained on"}
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {AI_TEAM.filter((m) => m.role === "analyst").map((m) => {
                const theme = ROLE_THEME[m.role];
                return (
                  <li
                    key={m.slug}
                    data-testid={`about-engine-analyst-${m.slug}`}
                    className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={m.portrait}
                        alt={isJa ? m.nameJa : m.nameEn}
                        className="h-12 w-12 rounded-full object-cover ring-2 ring-white/20 shrink-0"
                        loading="lazy"
                      />
                      <div>
                        <div className="text-[15px] font-bold text-white tracking-tight">
                          {isJa ? m.nameJa : m.nameEn}
                        </div>
                        <div
                          className="text-[10.5px] tracking-[0.18em] font-mono-display uppercase font-bold"
                          style={{ color: theme.hex }}
                        >
                          {isJa ? m.specJa : m.specEn}
                        </div>
                      </div>
                    </div>
                    <ul className="space-y-1.5">
                      {(isJa ? m.bulletsJa : m.bulletsEn).slice(0, 3).map((b, bi) => (
                        <li key={bi} className="flex items-start gap-2 text-[12.5px] text-white/75 leading-snug">
                          <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: theme.hex }} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Closing editorial row + CTA */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-7">
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[16/9]">
                <img
                  src={TEAM_WORKSPACE_PHOTOS[3].src}
                  alt={isJa ? TEAM_WORKSPACE_PHOTOS[3].captionJa : TEAM_WORKSPACE_PHOTOS[3].captionEn}
                  className="block w-full h-full object-cover"
                  loading="lazy"
                />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#5B2E83] via-[#4A2570]/30 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#7AB8E8] mb-1.5">
                    {isJa ? "私たちの姿勢" : "Our posture"}
                  </div>
                  <div className={[isJa ? "font-ja" : "font-display", "text-white text-2xl md:text-3xl font-bold tracking-tight leading-snug"].join(" ")}>
                    {isJa ? "革新 · 自動化 · 向上" : "Innovate · Automate · Elevate"}
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <h3 className={[isJa ? "font-ja" : "font-display", "text-white text-2xl md:text-[28px] font-bold tracking-tight leading-snug"].join(" ")}>
                {isJa
                  ? "ソラは最終確認に必要な情報を整理し、リンが検証し、3つのアナリストロールが処理を支援します。"
                  : "Sora decides. Rin verifies. Three analysts execute."}
              </h3>
              <p className="mt-3 text-[13.5px] leading-[1.8] text-white/85">
                {isJa
                  ? "同じ規律を、ETC・通行記録・両替の全領域に。だから多サービスでも品質がぶれません。"
                  : "Same discipline applied to ETC, toll records and the planned exchange service. That's how we keep quality consistent across multiple products."}
              </p>
              <Link
                to={`/${lang}/our-team`}
                data-testid="about-engine-meet-team"
                className="mt-6 inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-2.5 rounded-full text-[12.5px] font-semibold hover:bg-[#F1F5F9] transition-colors"
              >
                {isJa ? "AIチームを見る" : "Meet the AI team"}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK PATHS — surface Technology / Trust & Security / Contact
          for warm visitors who just learned about the company. */}
      <section
        data-testid="about-quick-paths"
        className="bg-white border-t border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="max-w-2xl mb-8 md:mb-10">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#475569] uppercase font-bold mb-3">
              {isJa ? "次の一歩" : "What's next"}
            </div>
            <h3
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-3xl font-bold tracking-tight leading-snug",
              ].join(" ")}
            >
              {isJa
                ? "ご関心に合わせて、もう一歩。"
                : "Pick the angle that matters to you."}
            </h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              {
                to: `/${lang}/technology`,
                tid: "about-quick-path-technology",
                accent: "#2563EB",
                tint: "#EFF6FF",
                Icon: Cpu,
                kicker: isJa ? "テクノロジー" : "Technology",
                title: isJa
                  ? "運用基盤と3つの柱"
                  : "Operating foundation & three pillars",
                body: isJa
                  ? "モビリティ・記録・デジタル基盤の3領域と、FastAPI・React・MongoDBによる運用基盤、AI支援チームの取り組み。"
                  : "How NoJo Enterprise builds: three pillars (mobility, records, digital infrastructure), the FastAPI + React + MongoDB foundation, and the AI-supported operations.",
              },
              {
                to: `/${lang}/trust-security`,
                tid: "about-quick-path-trust",
                accent: "#9333EA",
                tint: "#F5F3FF",
                Icon: ShieldCheck,
                kicker: isJa ? "信頼とセキュリティ" : "Trust & Security",
                title: isJa
                  ? "お客様データの取り扱い"
                  : "How we handle your data",
                body: isJa
                  ? "5つの運用原則と、お客様データに対する公開された運用方針（サービス間共有なし・監査ログ・最小限保持・透明な報告）。"
                  : "Five operating principles plus our published customer-data commitments — no cross-service sharing, audit-log discipline, minimal retention, transparent reporting.",
              },
              {
                to: `/${lang}/contact`,
                tid: "about-quick-path-contact",
                accent: "#16A34A",
                tint: "#F0FDF4",
                Icon: Mail,
                kicker: isJa ? "お問い合わせ" : "Talk to us",
                title: isJa
                  ? "ご質問・ご相談はこちら"
                  : "Got a question or scenario?",
                body: isJa
                  ? "個別のお問い合わせには、日本語または英語で営業日24時間以内に返信いたします。"
                  : "We reply to individual inquiries in Japanese or English within 24 hours on business days.",
              },
            ].map((c) => (
              <li key={c.tid}>
                <Link
                  to={c.to}
                  data-testid={c.tid}
                  className="group block h-full rounded-2xl ring-1 ring-[#E2E8F0] bg-white p-5 md:p-6 hover:-translate-y-0.5 hover:ring-[#0F1D2D]/20 hover:shadow-[0_10px_30px_-18px_rgba(15,29,45,0.45)] transition-all"
                >
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center h-10 w-10 rounded-md"
                    style={{ backgroundColor: c.tint }}
                  >
                    <c.Icon
                      className="h-5 w-5"
                      strokeWidth={1.7}
                      style={{ color: c.accent }}
                    />
                  </span>
                  <div
                    className="mt-3 text-[10px] tracking-[0.22em] font-mono-display font-bold uppercase"
                    style={{ color: c.accent }}
                  >
                    {c.kicker}
                  </div>
                  <h4
                    className={[
                      "mt-1.5",
                      isJa ? "font-ja" : "font-display",
                      "text-[#0F1D2D] text-[17px] font-bold tracking-tight leading-snug",
                    ].join(" ")}
                  >
                    {c.title}
                  </h4>
                  <p className="mt-2 text-[13.5px] leading-[1.8] text-[#475569]">
                    {c.body}
                  </p>
                  <span
                    className="mt-4 inline-flex items-center gap-1 text-[11px] tracking-[0.16em] font-mono uppercase font-bold transition-colors"
                    style={{ color: c.accent }}
                  >
                    {isJa ? "詳細を見る" : "Read more"}
                    <ArrowUpRight
                      className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      strokeWidth={2.25}
                    />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#0B4D8F] text-white overflow-hidden">
        <span aria-hidden className="absolute top-0 left-0 h-1 w-32 bg-[#5B2E83]" />
        <span aria-hidden className="absolute top-0 left-32 h-1 w-32 bg-[#2F8F3C]" />
        <span aria-hidden className="absolute top-0 left-64 h-1 w-32 bg-[#FFD166]" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <h3 className={["lg:col-span-7", isJa ? "font-ja" : "font-display", "text-white text-2xl md:text-3xl font-bold tracking-tight leading-snug"].join(" ")}>
            {isJa ? "私たちのサービスについて、もっと知る。" : "Want to dive deeper into the services?"}
          </h3>
          <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
            <Link to={`/${lang}/services`} className="inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-3 rounded-md text-[13px] font-semibold tracking-tight hover:bg-[#F1F5F9]" data-testid="about-cta-services">
              {isJa ? "サービスを見る" : "View services"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link to={`/${lang}/contact`} className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 rounded-md text-[13px] font-semibold tracking-tight hover:bg-white/10" data-testid="about-cta-contact">
              {isJa ? "お問い合わせ" : "Contact"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#F8F9FA] border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex items-start gap-4">
          <ShieldCheck className="h-5 w-5 text-[#64748B] shrink-0 mt-0.5" strokeWidth={1.6} />
          <p className="text-[12px] leading-[1.85] text-[#475569] max-w-4xl">
            {isJa
              ? "NoJo Enterpriseの運営サービスはすべて独立して運営されています。NEXCO・ETC利用照会サービス・金融機関・政府機関の公式サービスではありません。"
              : "All NoJo Enterprise services run independently. We are not NEXCO, the ETC Usage Inquiry Service, a financial institution, or any government agency."}
          </p>
        </div>
      </section>
    </article>
  );
}
